<template>
<div class="landing">
    <!-- Hero Section -->
    <section class="hero-section">
        <div class="hero-bg"></div>
        <div class="hero-content text-center relative z-10 py-20 px-4">
            <div class="inline-block mb-6 px-4 py-1.5 rounded-full bg-white/80 backdrop-blur border border-gray-200 text-sm text-gray-600 shadow-sm">
                Free Chrome Extension
            </div>
            <h1 class="text-5xl md:text-6xl font-extrabold mb-6 leading-tight tracking-tight">
                Download Videos &amp; Photos<br>
                from <span class="gradient-text">Any Website</span>
            </h1>
            <p class="text-xl text-gray-500 mb-10 max-w-2xl mx-auto leading-relaxed">
                Anyloader is a free Chrome extension that lets you download videos, photos, and audio from OnlyFans, Fansly, and 50+ other websites for offline viewing.
            </p>
            <div class="flex justify-center gap-4 flex-wrap mb-6">
                <a :href="`anyloader-${config.extensionVersion}.zip`">
                    <el-button type="primary" size="large" :icon="ElIconDownload" class="hero-btn hero-btn-primary">
                        Download Extension (v{{ config.extensionVersion }})
                    </el-button>
                </a>
                <router-link to="/onlyfans-downloader">
                    <el-button size="large" :icon="ElIconVideoPlay" class="hero-btn">
                        Try OnlyFans Downloader
                    </el-button>
                </router-link>
            </div>
            <p class="text-sm text-gray-400">Works with Chrome, Edge, Brave, and other Chromium-based browsers</p>
        </div>
        <svg class="hero-wave" viewBox="0 0 1440 120" preserveAspectRatio="none">
            <path d="M0,64 C360,120 720,0 1080,64 C1260,96 1380,80 1440,72 L1440,120 L0,120 Z" fill="white"/>
        </svg>
    </section>

    <!-- How It Works -->
    <section class="section-padded">
        <h2 class="section-title">How It Works</h2>
        <p class="section-subtitle">Get started in three simple steps</p>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div class="step-card" v-for="step in steps" :key="step.num">
                <div class="step-number">{{ step.num }}</div>
                <h3 class="text-lg font-semibold mb-2">{{ step.title }}</h3>
                <p class="text-gray-500 text-sm leading-relaxed">{{ step.description }}</p>
            </div>
        </div>
    </section>

    <!-- Wave: white -> alt -->
    <div class="wave-divider bg-alt">
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none"><path d="M0,0 C360,60 720,0 1080,40 C1260,55 1380,30 1440,20 L1440,0 Z" fill="white"/></svg>
    </div>

    <!-- Popular Downloaders -->
    <section class="section-padded section-alt">
        <h2 class="section-title">Popular Downloaders</h2>
        <p class="section-subtitle">Choose a platform to start downloading</p>
        <div class="dl-grid max-w-4xl mx-auto">
            <router-link v-for="dl in featuredDownloaders" :key="dl.name" :to="dl.link" class="dl-card-link">
                <div class="dl-card">
                    <div class="dl-avatar" :style="{ background: dl.color }">
                        {{ dl.name.charAt(0) }}
                    </div>
                    <div class="min-w-0 flex-1">
                        <h3 class="text-base font-semibold mb-0.5">{{ dl.name }}</h3>
                        <p class="text-sm text-gray-400 mb-0">{{ dl.description }}</p>
                    </div>
                    <ElIconArrowRight :width="16" class="ml-auto text-gray-300 flex-shrink-0" />
                </div>
            </router-link>
        </div>
        <div class="text-center mt-12">
            <router-link to="/all-downloaders">
                <el-button size="large" round class="!px-8">View All 50+ Supported Sites</el-button>
            </router-link>
        </div>
    </section>

    <!-- Wave: alt -> white -->
    <div class="wave-divider bg-white">
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none"><path d="M0,0 C240,60 600,0 960,40 C1150,55 1340,20 1440,10 L1440,0 Z" fill="#f0f5fa"/></svg>
    </div>

    <!-- Features -->
    <section class="section-padded">
        <h2 class="section-title">Powerful Features</h2>
        <p class="section-subtitle">Everything you need to save your favorite content</p>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <div class="feature-card" v-for="feature in features" :key="feature.title">
                <div class="feature-icon" :style="{ background: feature.bg }">
                    <component :is="feature.icon" :width="22" :style="{ color: feature.iconColor }" />
                </div>
                <div>
                    <h3 class="font-semibold mb-1">{{ feature.title }}</h3>
                    <p class="text-gray-500 text-sm leading-relaxed mb-0">{{ feature.description }}</p>
                </div>
            </div>
        </div>
    </section>

    <!-- Wave: white -> alt -->
    <div class="wave-divider bg-alt">
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none"><path d="M0,0 C180,60 540,0 900,40 C1100,55 1320,10 1440,20 L1440,0 Z" fill="white"/></svg>
    </div>

    <!-- Demo Video -->
    <section class="section-padded section-alt">
        <h2 class="section-title">See It In Action</h2>
        <p class="section-subtitle">Watch how easy it is to download content with Anyloader</p>
        <div class="max-w-2xl mx-auto text-center">
            <div v-if="!showDemoVideo" class="video-placeholder" @click="showDemoVideo = true">
                <div class="play-button">
                    <ElIconVideoPlay :width="32" class="text-white" />
                </div>
                <span class="text-gray-500 mt-4 text-sm">Click to play demo video</span>
            </div>
            <video v-show="showDemoVideo" controls class="w-full rounded-2xl shadow-xl">
                <source src="https://anyloader.com/onlyfans_downloader_demo.mp4" type="video/mp4">
                Your browser does not support the video tag.
            </video>
        </div>
    </section>

    <!-- Wave: alt -> white -->
    <div class="wave-divider bg-white">
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none"><path d="M0,0 C360,60 720,0 1080,40 C1260,55 1380,20 1440,10 L1440,0 Z" fill="#f0f5fa"/></svg>
    </div>

    <!-- Installation Guide -->
    <section class="section-padded">
        <h2 class="section-title">Quick Installation</h2>
        <p class="section-subtitle">Set up in under a minute</p>
        <div class="max-w-xl mx-auto">
            <div class="install-card">
                <ExtensionInfo />
                <div class="text-center mt-8">
                    <a :href="`anyloader-${config.extensionVersion}.zip`">
                        <el-button type="primary" size="large" :icon="ElIconDownload" round class="!px-8">
                            Download Extension
                        </el-button>
                    </a>
                </div>
            </div>
        </div>
    </section>

    <!-- Wave: white -> dark -->
    <div class="wave-divider bg-dark">
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none"><path d="M0,0 C360,60 720,0 1080,40 C1260,55 1380,20 1440,10 L1440,0 Z" fill="white"/></svg>
    </div>

    <!-- CTA -->
    <section class="cta-section">
        <div class="cta-content text-center relative z-10">
            <h2 class="text-3xl md:text-4xl font-bold mb-4 text-white">Ready to Start Downloading?</h2>
            <p class="text-white/70 mb-10 text-lg max-w-xl mx-auto">Install the extension and start saving your favorite content in seconds.</p>
            <div class="flex justify-center gap-4 flex-wrap">
                <a :href="`anyloader-${config.extensionVersion}.zip`">
                    <el-button size="large" round class="cta-btn cta-btn-white">
                        <ElIconDownload :width="18" class="mr-2" />
                        Download Free Extension
                    </el-button>
                </a>
                <router-link to="/onlyfans-downloader">
                    <el-button size="large" round class="cta-btn cta-btn-outline">
                        Go to OnlyFans Downloader
                    </el-button>
                </router-link>
            </div>
        </div>
    </section>
</div>
</template>

<script>
import { Download, VideoPlay, Lock, Monitor, ArrowRight } from '@element-plus/icons-vue'

export default {
    created() {
        useHead({
            title: 'Anyloader - Free Video & Photo Downloader for OnlyFans, Fansly & More',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: 'Anyloader is a free Chrome extension to download videos and photos from OnlyFans, Fansly, and 50+ websites. Bulk download posts, profiles, and chats for offline viewing.',
                },
                {
                    hid: 'keywords',
                    name: 'keywords',
                    content: 'onlyfans downloader, fansly downloader, video downloader, photo downloader, chrome extension, bulk download, download onlyfans video',
                }
            ],
            link: [
                {rel: 'canonical', href: useRuntimeConfig().public.siteUrl}
            ]
        })
    },
    setup() {
        const config = useConfig()
        return { config }
    },
    data () {
        return {
            showDemoVideo: false,
            steps: [
                { num: '1', title: 'Install the Extension', description: 'Download and install the Anyloader Chrome extension in your browser.' },
                { num: '2', title: 'Paste a Link', description: 'Copy the URL of the post, profile, or chat you want to download from.' },
                { num: '3', title: 'Download', description: 'Click download and save videos and photos directly to your device.' },
            ],
            featuredDownloaders: [
                { name: 'OnlyFans', description: 'Posts, profiles & chats', link: '/onlyfans-downloader', color: 'linear-gradient(135deg, #00AFF0, #0090D0)' },
                { name: 'Fansly', description: 'Posts, profiles & chats', link: '/fansly-downloader', color: 'linear-gradient(135deg, #1DA1F2, #0D8ECF)' },
                { name: 'Redgifs', description: 'GIFs & videos', link: '/redgifs-downloader', color: 'linear-gradient(135deg, #FF4500, #E03D00)' },
                { name: 'xHamster', description: 'Videos', link: '/xhamster-downloader', color: 'linear-gradient(135deg, #F59E0B, #D97706)' },
                { name: 'BiliBili', description: 'Videos', link: '/bilibili-downloader', color: 'linear-gradient(135deg, #FB7299, #E85D84)' },
                { name: 'Eporner', description: 'Videos', link: '/eporner-downloader', color: 'linear-gradient(135deg, #8B5CF6, #7C3AED)' },
            ],
            features: [
                {
                    title: 'Bulk Downloads',
                    description: 'Download entire profiles, chats, and posts at once instead of one file at a time.',
                    icon: Download,
                    bg: '#EFF6FF',
                    iconColor: '#3B82F6'
                },
                {
                    title: 'Video & Photo Support',
                    description: 'Download videos, photos, and audio files in their original quality.',
                    icon: VideoPlay,
                    bg: '#F0FDF4',
                    iconColor: '#22C55E'
                },
                {
                    title: 'Streaming Support',
                    description: 'Handles HLS and DASH streaming formats to save content in full quality.',
                    icon: Lock,
                    bg: '#FFF7ED',
                    iconColor: '#F97316'
                },
                {
                    title: 'Browser-based',
                    description: 'No desktop app needed. Everything runs in your browser with zero setup hassle.',
                    icon: Monitor,
                    bg: '#F5F3FF',
                    iconColor: '#8B5CF6'
                }
            ]
        }
    }
}
</script>

<style scoped>
.landing {
    margin: -20px;
    overflow: hidden;
    background: white;
}

.landing a {
    text-decoration: none;
    color: inherit;
}

.gradient-text {
    background: linear-gradient(135deg, #38bdf8, #34d399);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

/* Hero */
.hero-section {
    position: relative;
    overflow: hidden;
    background: #eef5ff;
}

.hero-bg {
    position: absolute;
    inset: 0;
    overflow: hidden;
    background:
        url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1440' height='560'%3E%3Cpath d='M0,320 C200,400 400,280 600,340 C800,400 1000,260 1200,320 C1320,350 1400,300 1440,310 L1440,560 L0,560 Z' fill='%2338bdf8' fill-opacity='0.07'/%3E%3Cpath d='M0,380 C180,440 420,300 660,380 C900,460 1100,320 1300,380 C1380,400 1440,370 1440,370 L1440,560 L0,560 Z' fill='%2334d399' fill-opacity='0.06'/%3E%3Cpath d='M0,200 C300,120 500,280 800,180 C1000,120 1200,240 1440,180 L1440,0 L0,0 Z' fill='%238b5cf6' fill-opacity='0.04'/%3E%3Cpath d='M0,100 C240,160 480,60 720,120 C960,180 1200,80 1440,120 L1440,0 L0,0 Z' fill='%2338bdf8' fill-opacity='0.05'/%3E%3C/svg%3E") center / cover no-repeat;
}

.hero-wave {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 80px;
    z-index: 5;
}

.hero-content {
    max-width: 800px;
    margin: 0 auto;
}

.hero-btn {
    font-size: 15px !important;
    padding: 22px 32px !important;
    border-radius: 12px !important;
    font-weight: 600 !important;
    letter-spacing: -0.01em;
}

.hero-btn-primary {
    box-shadow: 0 4px 14px rgba(64, 150, 255, 0.35) !important;
}

/* Sections */
.section-padded {
    padding: 64px 20px;
}

.section-alt {
    background: #f0f5fa;
}

/* Wave dividers */
.wave-divider {
    line-height: 0;
    margin: 0;
}

.wave-divider svg {
    display: block;
    width: 100%;
    height: 50px;
}

.wave-divider.bg-alt {
    background: #f0f5fa;
}

.wave-divider.bg-white {
    background: white;
}

.wave-divider.bg-dark {
    background: #0f172a;
}

.section-title {
    text-align: center;
    font-size: 2rem;
    font-weight: 800;
    margin-bottom: 8px;
    letter-spacing: -0.02em;
}

.section-subtitle {
    text-align: center;
    color: #9ca3af;
    margin-bottom: 40px;
    font-size: 1.05rem;
}

/* Steps */
.step-card {
    background: white;
    border-radius: 20px;
    padding: 36px 28px;
    text-align: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 6px 24px rgba(0, 0, 0, 0.03);
    border: 1px solid #f1f5f9;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.step-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06), 0 12px 32px rgba(0, 0, 0, 0.04);
}

.step-number {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: linear-gradient(135deg, #38bdf8, #34d399);
    color: white;
    font-size: 1.25rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
}

/* Downloader Cards */
.dl-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
}

@media (max-width: 767px) {
    .dl-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 479px) {
    .dl-grid {
        grid-template-columns: 1fr;
    }
}

.dl-card-link {
    text-decoration: none !important;
    color: inherit !important;
    display: block;
    height: 100%;
}

.dl-card {
    display: flex;
    align-items: center;
    gap: 14px;
    background: white;
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 16px rgba(0, 0, 0, 0.02);
    border: 1px solid #f1f5f9;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    height: 100%;
    box-sizing: border-box;
    position: relative;
    z-index: 1;
}

.dl-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.04);
    z-index: 2;
}

.dl-avatar {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 700;
    font-size: 1.1rem;
    flex-shrink: 0;
}

/* Feature Cards */
.feature-card {
    display: flex;
    gap: 16px;
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 16px rgba(0, 0, 0, 0.02);
    border: 1px solid #f1f5f9;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.feature-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.04);
}

.feature-icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

/* Video Placeholder */
.video-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: white;
    border: 2px dashed #e2e8f0;
    border-radius: 20px;
    padding: 60px 20px;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease;
}

.video-placeholder:hover {
    border-color: #38bdf8;
    background: #f8fafc;
}

.play-button {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: linear-gradient(135deg, #38bdf8, #34d399);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 20px rgba(56, 189, 248, 0.3);
    transition: transform 0.2s ease;
}

.video-placeholder:hover .play-button {
    transform: scale(1.08);
}

/* Install Card */
.install-card {
    background: white;
    border-radius: 20px;
    padding: 36px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 32px rgba(0, 0, 0, 0.04);
    border: 1px solid #f1f5f9;
}

/* CTA Section */
.cta-section {
    background: #0f172a;
    padding: 64px 20px;
    position: relative;
    overflow: hidden;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1440' height='400'%3E%3Cpath d='M0,200 C300,280 500,120 800,200 C1100,280 1300,140 1440,200 L1440,400 L0,400 Z' fill='%2338bdf8' fill-opacity='0.06'/%3E%3Cpath d='M0,260 C200,320 500,180 800,260 C1050,310 1300,200 1440,260 L1440,400 L0,400 Z' fill='%2334d399' fill-opacity='0.05'/%3E%3C/svg%3E");
    background-size: cover;
    background-position: center;
}

.cta-btn {
    font-size: 15px !important;
    padding: 22px 32px !important;
    font-weight: 600 !important;
    border-radius: 50px !important;
}

.cta-btn-white {
    background: white !important;
    color: #0f172a !important;
    border: none !important;
    box-shadow: 0 4px 14px rgba(255, 255, 255, 0.15) !important;
}

.cta-btn-white:hover {
    background: #f1f5f9 !important;
}

.cta-btn-outline {
    background: transparent !important;
    color: white !important;
    border: 1.5px solid rgba(255, 255, 255, 0.3) !important;
}

.cta-btn-outline:hover {
    border-color: rgba(255, 255, 255, 0.6) !important;
    background: rgba(255, 255, 255, 0.05) !important;
}
</style>
